import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersApi } from '@/features/users/api/usersApi';
import { User } from '@/types/user';

/**
 * Custom hook for managing user profile with TanStack Query.
 */
export const useUserProfile = (userId: string | null) => {
  const queryClient = useQueryClient();

  // Fetch user profile
  const { data: user, isLoading, isError, error } = useQuery<User>({
    queryKey: ['userProfile', userId],
    queryFn: () => usersApi.getById(userId!),
    enabled: !!userId, // Only run if userId exists
    retry: 1,
  });

  // Update user profile mutation
  const updateMutation = useMutation({
    mutationFn: (data: Partial<User>) => usersApi.update(userId!, data),
    onSuccess: () => {
      // Invalidate queries to refetch
      queryClient.invalidateQueries({ queryKey: ['userProfile', userId] });
    },
  });

  return {
    user: user || null,
    isLoading,
    isError,
    error,
    updateProfile: (data: Partial<User>) => updateMutation.mutateAsync(data),
    isUpdating: updateMutation.isPending,
    updateError: updateMutation.error,
  };
};
